import React, { useState, useEffect } from 'react';
import { Button, Label, Select, TextInput, Textarea, Card, Radio, Spinner } from 'flowbite-react';
import { observer } from 'mobx-react-lite';
import { HiSave, HiInformationCircle } from 'react-icons/hi';
import { tenantStore } from '../stores/tenantStore';
import { toastStore } from '../stores/toastStore';
import { LLMConfig } from '../types';

interface LLMSettingsTabProps {
  tenantId: string;
}

const LLMSettingsTab: React.FC<LLMSettingsTabProps> = observer(({ tenantId }) => {
  const [config, setConfig] = useState<LLMConfig>({
    provider: 'openai',
    model: 'gpt-5-mini',
    reasoningEffort: 'minimal',
    verbosity: 'low',
    maxCompletionTokens: 2048,
    useStructuredOutput: true,  // デフォルトで構造化出力を有効化
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [schemaError, setSchemaError] = useState<string>('');  // JSONスキーマのエラー

  // Default prompt template
  const defaultPrompt = `この画像を読み込み、以下のJSONフォーマットに基づいて出力すること。
メタデータなどは出力せず、JSONのみを出力すること。

[{ "room": string, "name": string, "calling": string, "waiting": string[] }]`;

  // デフォルトのJSONスキーマ
  const defaultSchema = `{
  "type": "array",
  "items": {
    "type": "object",
    "properties": {
      "room": { "type": "string", "description": "診察室・窓口番号" },
      "name": { "type": "string", "description": "診療科・窓口名" },
      "calling": { "type": "string", "description": "現在呼び出し中の番号" },
      "waiting": {
        "type": "array",
        "items": { "type": "string" },
        "description": "待機中の番号リスト"
      }
    },
    "required": ["room", "name", "calling", "waiting"]
  }
}`;

  // Official model catalogs checked on 2026-10-19:
  // https://developers.openai.com/api/docs/models
  // https://ai.google.dev/gemini-api/docs/models
  const reasoningModels = ['gpt-6-astra', 'gpt-6.1-sol', 'gpt-6-luna', 'gpt-5', 'gpt-5-mini', 'gpt-5-nano'];
  const geminiModels = [
    'gemini-3.8-flash', 'gemini-3.6-flash', 'gemini-3.5-flash-lite', 'gemini-3.1-flash-lite',
    'gemini-3.1-pro-preview',
    'gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.5-flash-lite', 'gemini-2.0-flash',
    'gemini-3-flash-preview', 'gemini-3.1-flash-lite-preview',
  ];

  const isReasoningModel = (model: string) => reasoningModels.includes(model);
  const isGeminiModel = (model: string) => geminiModels.includes(model);

  // 構造化出力対応モデルの判定
  const isStructuredOutputSupported = (model: string, provider: string) => {
    if (provider === 'google') {
      // Geminiは全モデル対応
      return true;
    }
    // OpenAIは特定モデルのみ対応
    return isReasoningModel(model) || model === 'gpt-4o-mini' || model === 'gpt-4o';
  };

  // JSONスキーマのバリデーション
  const validateJSONSchema = (schemaString: string): boolean => {
    if (!schemaString || schemaString.trim() === '') {
      setSchemaError('');
      return true; // 空の場合はデフォルトを使用
    }
    try {
      const parsed = JSON.parse(schemaString);
      // 基本的な構造チェック
      if (!parsed.type) {
        setSchemaError('スキーマには"type"プロパティが必要です');
        return false;
      }
      setSchemaError('');
      return true;
    } catch (e) {
      setSchemaError('無効なJSON形式です');
      return false;
    }
  };

  const handleSchemaChange = (newSchema: string) => {
    setConfig({ ...config, responseSchema: newSchema });
    validateJSONSchema(newSchema);
  };

  useEffect(() => {
    loadLLMSettings();
  }, [tenantId]);

  const loadLLMSettings = async () => {
    setIsLoading(true);
    try {
      const settings = await tenantStore.loadLLMSettings(tenantId);
      // デフォルト値とマージして、明示的にfalseの値も保持
      setConfig({
        provider: settings.provider || 'openai',
        model: settings.model || 'gpt-5-mini',
        reasoningEffort: settings.reasoningEffort || 'minimal',
        verbosity: settings.verbosity || 'low',
        maxCompletionTokens: settings.maxCompletionTokens || 2048,
        temperature: settings.temperature,
        maxTokens: settings.maxTokens,
        maxOutputTokens: settings.maxOutputTokens,
        promptTemplate: settings.promptTemplate || '',
        useStructuredOutput: settings.useStructuredOutput !== undefined ? settings.useStructuredOutput : true,
        structuredPrompt: settings.structuredPrompt || '',
        responseSchema: settings.responseSchema || '',
      });
    } catch (err) {
      toastStore.showError('LLM設定の読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await tenantStore.updateLLMSettings(tenantId, config);
      toastStore.showSuccess('LLM設定を保存しました');
    } catch (err) {
      toastStore.showError('LLM設定の保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  const handleProviderChange = (provider: string) => {
    const newConfig: LLMConfig = { ...config, provider: provider as 'openai' | 'google' };

    // プロバイダーに応じてデフォルトモデルを設定
    if (provider === 'google') {
      newConfig.model = 'gemini-2.5-flash';
      newConfig.temperature = 0.3;
      delete newConfig.reasoningEffort;
      delete newConfig.verbosity;
      delete newConfig.maxCompletionTokens;
      newConfig.maxOutputTokens = 2048;
    } else {
      newConfig.model = 'gpt-5-mini';
      newConfig.reasoningEffort = 'minimal';
      newConfig.verbosity = 'low';
      newConfig.maxCompletionTokens = 2048;
      delete newConfig.maxOutputTokens;
    }

    setConfig(newConfig);
  };

  const handleModelChange = (model: string) => {
    const newConfig: LLMConfig = { ...config, model: model as LLMConfig['model'] };

    // モデルタイプに応じてデフォルトパラメータを設定
    if (isGeminiModel(model)) {
      // Gemini系のデフォルト
      newConfig.temperature = newConfig.temperature || 0.3;
      newConfig.maxOutputTokens = newConfig.maxOutputTokens || 2048;
      // GPT系パラメータをクリア
      delete newConfig.reasoningEffort;
      delete newConfig.verbosity;
      delete newConfig.maxCompletionTokens;
      delete newConfig.maxTokens;
    } else if (isReasoningModel(model)) {
      // GPT-5/6系のデフォルト
      newConfig.reasoningEffort = newConfig.reasoningEffort || 'medium';
      if (model.startsWith('gpt-6') && newConfig.reasoningEffort === 'minimal') {
        newConfig.reasoningEffort = 'low';
      }
      newConfig.verbosity = newConfig.verbosity || 'medium';
      newConfig.maxCompletionTokens = newConfig.maxCompletionTokens || 500;
      // GPT-4系パラメータをクリア
      delete newConfig.temperature;
      delete newConfig.maxTokens;
    } else {
      // GPT-4系のデフォルト
      newConfig.temperature = newConfig.temperature || 0.3;
      newConfig.maxTokens = newConfig.maxTokens || 500;
      // GPT-5/6系パラメータをクリア
      delete newConfig.reasoningEffort;
      delete newConfig.verbosity;
      delete newConfig.maxCompletionTokens;
    }
    
    setConfig(newConfig);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* プロバイダー選択 */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">LLMプロバイダー選択</h3>
        <div className="space-y-4">
          <div>
            <Label htmlFor="provider" value="LLMプロバイダー" />
            <Select
              id="provider"
              value={config.provider}
              onChange={(e) => handleProviderChange(e.target.value)}
              required
            >
              <option value="openai">OpenAI</option>
              <option value="google">Google</option>
            </Select>
          </div>

          <div className="flex items-start space-x-2 p-3 bg-blue-50 rounded-lg">
            <HiInformationCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              {config.provider === 'google' ? (
                <p>Google Gemini APIを使用します。高速で効率的な画像解析が可能です。</p>
              ) : (
                <p>OpenAI APIを使用します。GPT-6系などのモデルで高精度な画像解析が可能です。</p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* モデル選択 */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">モデル選択</h3>
        <div className="space-y-4">
          <div>
            <Label htmlFor="model" value="使用するLLMモデル" />
            <Select
              id="model"
              value={config.model}
              onChange={(e) => handleModelChange(e.target.value)}
              required
            >
              {config.provider === 'google' ? (
                <>
                  <optgroup label="Gemini 3系（安定版）">
                    <option value="gemini-3.8-flash">Gemini 3.8 Flash</option>
                    <option value="gemini-3.6-flash">Gemini 3.6 Flash</option>
                    <option value="gemini-3.5-flash-lite">Gemini 3.5 Flash-Lite</option>
                    <option value="gemini-3.1-flash-lite">Gemini 3.1 Flash-Lite</option>
                  </optgroup>
                  <optgroup label="Gemini 3系（プレビュー）">
                    <option value="gemini-3.1-pro-preview">Gemini 3.1 Pro Preview</option>
                    <option value="gemini-3-flash-preview">Gemini 3 Flash Preview</option>
                    {config.model === 'gemini-3.1-flash-lite-preview' && (
                      <option value="gemini-3.1-flash-lite-preview">Gemini 3.1 Flash-Lite Preview（旧モデル）</option>
                    )}
                  </optgroup>
                  <optgroup label="Gemini 2.5系">
                    <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
                    <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                    <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash-Lite（軽量版）</option>
                  </optgroup>
                  {config.model === 'gemini-2.0-flash' && (
                    <optgroup label="旧モデル（既存設定）">
                      <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                    </optgroup>
                  )}
                </>
              ) : (
                <>
                  <optgroup label="GPT-6系">
                    <option value="gpt-6.1-sol">GPT-6.1 Sol</option>
                    <option value="gpt-6-astra">GPT-6 Astra</option>
                    <option value="gpt-6-luna">GPT-6 Luna</option>
                  </optgroup>
                  <optgroup label="GPT-5系">
                    <option value="gpt-5-mini">gpt-5-mini（デフォルト・コスト効率重視）</option>
                    <option value="gpt-5-nano">gpt-5-nano（最も低コスト）</option>
                    <option value="gpt-5">gpt-5</option>
                  </optgroup>
                  <optgroup label="GPT-4.1系（安定版）">
                    <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                    <option value="gpt-4.1-nano">gpt-4.1-nano</option>
                    <option value="gpt-4.1">gpt-4.1</option>
                  </optgroup>
                  <optgroup label="GPT-4o系（レガシー）">
                    <option value="gpt-4o-mini">gpt-4o-mini</option>
                    <option value="gpt-4o">gpt-4o</option>
                  </optgroup>
                </>
              )}
            </Select>
          </div>

          <div className="flex items-start space-x-2 p-3 bg-blue-50 rounded-lg">
            <HiInformationCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              {isGeminiModel(config.model) ? (
                <p>Gemini系モデルは高速で効率的な処理が可能で、temperatureとmax_output_tokensで出力を制御します。</p>
              ) : isReasoningModel(config.model) ? (
                <p>GPT-5/6系モデルはreasoning_effortとverbosityで出力を制御します。</p>
              ) : (
                <p>GPT-4系モデルは従来のtemperatureとmax_tokensパラメータで出力を制御します。</p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* パラメータ設定 */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">パラメータ設定</h3>
        <div className="space-y-4">
          {isGeminiModel(config.model) ? (
            <>
              {/* Gemini系パラメータ */}
              <div>
                <Label htmlFor="temperature" value="Temperature（創造性）" />
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    id="temperature"
                    min="0"
                    max="1"
                    step="0.1"
                    value={config.temperature || 0.3}
                    onChange={(e) => setConfig({ ...config, temperature: parseFloat(e.target.value) })}
                    className="flex-1"
                  />
                  <span className="w-12 text-center font-mono">
                    {(config.temperature || 0.3).toFixed(1)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  0.0（決定的）〜 1.0（創造的）- デフォルト: 0.3
                </p>
              </div>

              <div>
                <Label htmlFor="maxOutputTokens" value="Max Output Tokens" />
                <TextInput
                  id="maxOutputTokens"
                  type="number"
                  min={100}
                  max={8192}
                  value={config.maxOutputTokens || 2048}
                  onChange={(e) => setConfig({ ...config, maxOutputTokens: parseInt(e.target.value) })}
                  helperText="100〜8192の範囲で指定してください"
                />
              </div>
            </>
          ) : isReasoningModel(config.model) ? (
            <>
              {/* GPT-5/6系パラメータ */}
              <div>
                <Label value="Reasoning Effort（思考の深さ）" />
                <div className="space-y-2 mt-2">
                  <div className="flex items-center">
                    <Radio
                      id="minimal"
                      name="reasoningEffort"
                      value="minimal"
                      disabled={config.model.startsWith('gpt-6')}
                      checked={config.reasoningEffort === 'minimal'}
                      onChange={(e) => setConfig({ ...config, reasoningEffort: e.target.value as any })}
                    />
                    <Label htmlFor="minimal" className="ml-2">
                      Minimal - 最速・最小コスト
                    </Label>
                  </div>
                  <div className="flex items-center">
                    <Radio
                      id="low"
                      name="reasoningEffort"
                      value="low"
                      checked={config.reasoningEffort === 'low'}
                      onChange={(e) => setConfig({ ...config, reasoningEffort: e.target.value as any })}
                    />
                    <Label htmlFor="low" className="ml-2">
                      Low - 軽い思考、ツール使用向け
                    </Label>
                  </div>
                  <div className="flex items-center">
                    <Radio
                      id="medium"
                      name="reasoningEffort"
                      value="medium"
                      checked={config.reasoningEffort === 'medium'}
                      onChange={(e) => setConfig({ ...config, reasoningEffort: e.target.value as any })}
                    />
                    <Label htmlFor="medium" className="ml-2">
                      Medium - バランス型（推奨）
                    </Label>
                  </div>
                  <div className="flex items-center">
                    <Radio
                      id="high"
                      name="reasoningEffort"
                      value="high"
                      checked={config.reasoningEffort === 'high'}
                      onChange={(e) => setConfig({ ...config, reasoningEffort: e.target.value as any })}
                    />
                    <Label htmlFor="high" className="ml-2">
                      High - 複雑な問題向け（コスト増）
                    </Label>
                  </div>
                </div>
              </div>

              <div>
                <Label value="Verbosity（出力の詳細度）" />
                <div className="space-y-2 mt-2">
                  <div className="flex items-center">
                    <Radio
                      id="v-low"
                      name="verbosity"
                      value="low"
                      checked={config.verbosity === 'low'}
                      onChange={(e) => setConfig({ ...config, verbosity: e.target.value as any })}
                    />
                    <Label htmlFor="v-low" className="ml-2">
                      Low - 簡潔な回答
                    </Label>
                  </div>
                  <div className="flex items-center">
                    <Radio
                      id="v-medium"
                      name="verbosity"
                      value="medium"
                      checked={config.verbosity === 'medium'}
                      onChange={(e) => setConfig({ ...config, verbosity: e.target.value as any })}
                    />
                    <Label htmlFor="v-medium" className="ml-2">
                      Medium - 標準（デフォルト）
                    </Label>
                  </div>
                  <div className="flex items-center">
                    <Radio
                      id="v-high"
                      name="verbosity"
                      value="high"
                      checked={config.verbosity === 'high'}
                      onChange={(e) => setConfig({ ...config, verbosity: e.target.value as any })}
                    />
                    <Label htmlFor="v-high" className="ml-2">
                      High - 詳細な説明
                    </Label>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="maxCompletionTokens" value="Max Completion Tokens" />
                <TextInput
                  id="maxCompletionTokens"
                  type="number"
                  min={100}
                  max={4000}
                  value={config.maxCompletionTokens || 500}
                  onChange={(e) => setConfig({ ...config, maxCompletionTokens: parseInt(e.target.value) })}
                  helperText="100〜4000の範囲で指定してください"
                />
              </div>
            </>
          ) : (
            <>
              {/* GPT-4系パラメータ */}
              <div>
                <Label htmlFor="temperature" value="Temperature（創造性）" />
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    id="temperature"
                    min="0"
                    max="1"
                    step="0.1"
                    value={config.temperature || 0.3}
                    onChange={(e) => setConfig({ ...config, temperature: parseFloat(e.target.value) })}
                    className="flex-1"
                  />
                  <span className="w-12 text-center font-mono">
                    {(config.temperature || 0.3).toFixed(1)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  0.0（決定的）〜 1.0（創造的）
                </p>
              </div>

              <div>
                <Label htmlFor="maxTokens" value="Max Tokens" />
                <TextInput
                  id="maxTokens"
                  type="number"
                  min={100}
                  max={4000}
                  value={config.maxTokens || 500}
                  onChange={(e) => setConfig({ ...config, maxTokens: parseInt(e.target.value) })}
                  helperText="100〜4000の範囲で指定してください"
                />
              </div>
            </>
          )}
        </div>
      </Card>

      {/* 出力モード設定 */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">出力モード設定</h3>
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start">
              <Radio
                id="structured"
                name="outputMode"
                value="structured"
                checked={config.useStructuredOutput !== false}
                onChange={() => setConfig({ ...config, useStructuredOutput: true })}
                disabled={!isStructuredOutputSupported(config.model, config.provider || 'openai')}
              />
              <div className="ml-3">
                <Label htmlFor="structured" className="font-medium">
                  構造化出力（推奨）
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  確実にJSON形式で応答を取得。エラーが激減し、処理が高速化します。
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Radio
                id="traditional"
                name="outputMode"
                value="traditional"
                checked={config.useStructuredOutput === false}
                onChange={() => setConfig({ ...config, useStructuredOutput: false })}
              />
              <div className="ml-3">
                <Label htmlFor="traditional" className="font-medium">
                  従来のプロンプトベース出力
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  カスタムプロンプトで柔軟な制御が可能です。
                </p>
              </div>
            </div>
          </div>

          {/* 構造化出力の互換性表示 */}
          {!isStructuredOutputSupported(config.model, config.provider || 'openai') && (
            <div className="flex items-start space-x-2 p-3 bg-yellow-50 rounded-lg">
              <HiInformationCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold">⚠️ 構造化出力非対応モデル</p>
                <p className="mt-1">
                  選択中のモデル「{config.model}」は構造化出力に対応していません。
                  従来のプロンプトベース出力を使用してください。
                </p>
                <p className="mt-2 font-medium">対応モデル：</p>
                <ul className="mt-1 ml-4 list-disc">
                  <li>OpenAI: gpt-4o-mini, gpt-4o-2024-08-06以降, GPT-5/6系</li>
                  <li>Google: すべてのGeminiモデル</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* プロンプトテンプレート / 構造化出力設定 */}
      {config.useStructuredOutput && isStructuredOutputSupported(config.model, config.provider || 'openai') ? (
        /* 構造化出力モード */
        <>
          {/* スキーマ編集 */}
          <Card>
            <h3 className="text-lg font-semibold mb-4">出力スキーマ定義</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="responseSchema" value="JSONスキーマ" />
                <div className="space-y-2">
                  <Textarea
                    id="responseSchema"
                    rows={12}
                    value={config.responseSchema || ''}
                    onChange={(e) => handleSchemaChange(e.target.value)}
                    placeholder={defaultSchema}
                    className={`font-mono text-xs ${schemaError ? 'border-red-500' : ''}`}
                  />
                  {schemaError && (
                    <p className="text-sm text-red-600 flex items-center">
                      <HiInformationCircle className="h-4 w-4 mr-1" />
                      {schemaError}
                    </p>
                  )}
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-500">
                      JSONスキーマ形式で出力構造を定義します。空欄の場合はデフォルトスキーマを使用します。
                    </p>
                    <Button
                      size="xs"
                      color="gray"
                      onClick={() => {
                        setConfig({ ...config, responseSchema: defaultSchema });
                        setSchemaError('');
                      }}
                    >
                      デフォルトに戻す
                    </Button>
                  </div>
                </div>
              </div>

              {/* スキーマプレビュー */}
              {(config.responseSchema || defaultSchema) && !schemaError && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">使用されるスキーマ：</p>
                  <pre className="bg-white p-3 rounded border border-gray-200 text-xs font-mono overflow-x-auto">
                    {JSON.stringify(JSON.parse(config.responseSchema || defaultSchema), null, 2)}
                  </pre>
                </div>
              )}

              {/* 期待される出力例 */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex items-start space-x-2">
                  <HiInformationCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-2">スキーマ定義のヒント：</p>
                    <ul className="ml-4 list-disc space-y-1">
                      <li>"type": データ型を指定（string, number, boolean, array, object）</li>
                      <li>"properties": オブジェクトのプロパティを定義</li>
                      <li>"required": 必須フィールドを配列で指定</li>
                      <li>"description": フィールドの説明（AIへのヒント）</li>
                      <li>"items": 配列の要素構造を定義</li>
                    </ul>
                  </div>
                </div>
              </div>


              {/* 期待される出力例 */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">システムで必要な出力例：</p>
                <pre className="bg-white p-2 rounded border text-xs overflow-x-auto">
{`[
  {
    "room": "診察室1",
    "name": "内科",
    "calling": "A001",
    "waiting": ["A002", "A003", "A004"]
  },
  {
    "room": "診察室2",
    "name": "耳鼻科",
    "calling": "B005",
    "waiting": ["B006", "B007"]
  }
]`}
                </pre>
              </div>
            </div>
          </Card>

          {/* 簡潔な指示入力 */}
          <Card>
            <h3 className="text-lg font-semibold mb-4">画像解析の指示（オプション）</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-2 p-3 bg-blue-50 rounded-lg">
                <HiInformationCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p>
                    構造化出力モードではJSONフォーマットの指定が不要です。
                    画像をどのように解析するかの簡潔な指示のみ記載してください。
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="structuredPrompt" value="解析指示（500文字以内）" />
                <Textarea
                  id="structuredPrompt"
                  rows={4}
                  maxLength={500}
                  value={config.structuredPrompt || ''}
                  onChange={(e) => setConfig({ ...config, structuredPrompt: e.target.value })}
                  placeholder="待合室の画面から呼び出し情報を抽出してください"
                  className="font-mono text-sm"
                />
                <div className="flex justify-between mt-2">
                  <p className="text-sm text-gray-500">
                    空欄の場合はデフォルトの指示を使用します
                  </p>
                  <p className="text-sm text-gray-500">
                    {config.structuredPrompt?.length || 0} / 500
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </>
      ) : (
        /* 従来のプロンプトベースモード */
        <Card>
          <h3 className="text-lg font-semibold mb-4">プロンプトテンプレート</h3>
          <div className="space-y-4">
          {/* デフォルトプロンプトの表示 */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-start space-x-2">
              <HiInformationCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">デフォルトプロンプト：</p>
                <pre className="bg-white p-2 rounded border border-blue-200 whitespace-pre-wrap font-mono text-xs">
                  {defaultPrompt}
                </pre>
              </div>
            </div>
          </div>

          {/* カスタムプロンプト入力 */}
          <div>
            <Label htmlFor="promptTemplate" value="カスタムプロンプト（オプション）" />
            <Textarea
              id="promptTemplate"
              rows={8}
              value={config.promptTemplate || ''}
              onChange={(e) => setConfig({ ...config, promptTemplate: e.target.value })}
              placeholder={defaultPrompt}
              className="font-mono text-sm"
            />
            <p className="text-sm text-gray-500 mt-2">
              画像分析用のカスタムプロンプトを指定できます。空欄の場合は上記のデフォルトプロンプトが使用されます。
            </p>
          </div>

          {/* JSONフォーマット例 */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-2">システムで必要なJSONレスポンス例：</p>
            <pre className="bg-white p-2 rounded border text-xs overflow-x-auto">
{`[
  {
    "room": "診察室1",
    "name": "内科",
    "calling": "A001",
    "waiting": ["A002", "A003", "A004"]
  },
  {
    "room": "診察室2",
    "name": "耳鼻科",
    "calling": "B005",
    "waiting": ["B006", "B007"]
  }
]`}
            </pre>
          </div>

            {/* 使用中のプロンプトプレビュー */}
            {config.promptTemplate && (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm font-medium text-green-800 mb-2">使用されるプロンプト：</p>
                <pre className="bg-white p-2 rounded border border-green-200 whitespace-pre-wrap font-mono text-xs">
                  {config.promptTemplate}
                </pre>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* 保存ボタン */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving || !!schemaError}>
          <HiSave className="mr-2 h-4 w-4" />
          {isSaving ? '保存中...' : '設定を保存'}
        </Button>
      </div>
    </div>
  );
});

export default LLMSettingsTab;
